import type { Container } from 'pixi.js';
import { PickupKind, POOL_CAPACITY } from '../../../constants';
import type { GameWorld } from '../../../world/GameWorld';
import type { LayerStackSprite } from '../../layerstack/LayerStackSprite';
import type { MobilePoolSnapshot } from '../MobilePoolObserver';
import { PICKUP_SLUG } from '../assets/registry';
import type { StackRegistry } from '../assets/StackRegistry';
import { placeSprite } from '../scale';
import type { EntityFamily, FamilyDeps, FrameContext } from './types';

const NEUTRAL_TINT = 0xffffff;
/** Power has no authored per-kind state (the glyph ships `neutral` only), so
 * kind is read off tint + a stronger alpha breathe instead of a state swap. */
const POWER_TINT = 0xff5fa8;
const POWER_PULSE_PERIOD_TICKS = 24;
const POWER_PULSE_ALPHA_MIN = 0.55;
const POWER_PULSE_ALPHA_MAX = 1;
const TAU = Math.PI * 2;
const MOBILE_PICKUP_PREWARM = 12;

/** One sprite per pickup slot; a pickup has a single authored state. */
export class PickupsFamily implements EntityFamily {
  private stacks: StackRegistry | null = null;
  private layer: Container | null = null;
  private mobilePerformanceMode = false;
  private readonly sprites: (LayerStackSprite | null)[] = [];
  private mobilePool: MobilePoolSnapshot | null = null;

  init(deps: FamilyDeps): void {
    this.stacks = deps.stacks;
    this.layer = deps.layer;
    this.mobilePerformanceMode = deps.mobilePerformanceMode;
    this.mobilePool = deps.mobilePools?.pickups ?? null;
    if (this.mobilePerformanceMode) {
      this.sprites.length = POOL_CAPACITY.pickups;
      this.sprites.fill(null);
      deps.stacks.prewarm(PICKUP_SLUG, MOBILE_PICKUP_PREWARM);
      return;
    }
    for (let i = 0; i < POOL_CAPACITY.pickups; i += 1) {
      const sprite = deps.stacks.acquire(PICKUP_SLUG, deps.layer);
      sprite.visible = false;
      this.sprites.push(sprite);
    }
  }

  sync(world: GameWorld, _frame: FrameContext): void {
    if (this.mobilePerformanceMode) {
      this.syncMobile(world);
      return;
    }
    const pool = world.pickups;
    for (let i = 0; i < this.sprites.length; i += 1) {
      const sprite = this.sprites[i]!;
      sprite.visible = pool.indices.active[i] !== 0;
      if (!sprite.visible) continue;
      placeSprite(sprite, pool.x[i]!, pool.y[i]!);
      if (pool.kind[i] === PickupKind.Power) {
        sprite.setTint(POWER_TINT);
        const phase = (world.simTick % POWER_PULSE_PERIOD_TICKS) / POWER_PULSE_PERIOD_TICKS;
        sprite.alpha =
          POWER_PULSE_ALPHA_MIN + (POWER_PULSE_ALPHA_MAX - POWER_PULSE_ALPHA_MIN) * (0.5 + 0.5 * Math.sin(phase * TAU));
      } else {
        sprite.setTint(NEUTRAL_TINT);
        sprite.alpha = 1;
      }
    }
  }

  dispose(): void {
    this.sprites.length = 0;
    this.mobilePool = null;
    this.mobilePerformanceMode = false;
    this.layer = null;
    this.stacks = null;
  }

  private syncMobile(world: GameWorld): void {
    const stacks = this.stacks;
    const layer = this.layer;
    const observed = this.mobilePool;
    if (!stacks || !layer || !observed) return;
    const pool = world.pickups;

    for (let i = 0; i < observed.deactivatedCount; i += 1) {
      const slot = observed.deactivatedSlots[i]!;
      const sprite = this.sprites[slot];
      if (!sprite) continue;
      stacks.release(PICKUP_SLUG, sprite);
      this.sprites[slot] = null;
    }

    for (let i = 0; i < observed.activeCount; i += 1) {
      const slot = observed.activeSlots[i]!;
      let sprite = this.sprites[slot];
      if (!sprite) {
        sprite = stacks.acquire(PICKUP_SLUG, layer);
        this.sprites[slot] = sprite;
      }
      placeSprite(sprite, pool.x[slot]!, pool.y[slot]!);
      if (pool.kind[slot] === PickupKind.Power) {
        sprite.setTint(POWER_TINT);
        const phase = (world.simTick % POWER_PULSE_PERIOD_TICKS) / POWER_PULSE_PERIOD_TICKS;
        sprite.alpha =
          POWER_PULSE_ALPHA_MIN + (POWER_PULSE_ALPHA_MAX - POWER_PULSE_ALPHA_MIN) * (0.5 + 0.5 * Math.sin(phase * TAU));
      } else {
        sprite.setTint(NEUTRAL_TINT);
        sprite.alpha = 1;
      }
    }
  }
}
